
import {defineComponent} from "vue";
import {useToast} from "vue-toastification";
import {isEmptyString, truncateString} from "@/helpers/functions/string";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import InputCheckboxMultiSelect from "@/components/UI/InputCheckboxMultiSelect.vue";
import InputText from "@/components/UI/InputText.vue";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import {
  FullPopupRestDto,
  MultilingualMax50FieldRestDto,
  PopupAssociationOverviewRestDto,
  PopupAssociationOverviewResultsRestDto,
  PopupTriggerOverviewRestDto,
  PopupTriggerOverviewResultsRestDto,
  PopupVisibilityRuleOverviewResultsRestDto,
  PopupVisibilityRulesOverviewRestDto
} from "@/api/pharma-cms-popup/models";
import {PopupRestService} from "@/services/rest/cms-popup/PopupRestService";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {CodeDescriptionRestDto} from "@/api/pharma-cpc-mgmt/models";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import BaseOffcanvasButtonOpen from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonOpen.vue";
import BaseOffcanvas from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvas.vue";
import Trigger from "@/components/layouts/content-pages/popups/Trigger.vue";
import {PopupTriggerTypeEnum} from "@/api/enums/popup-trigger-type-enum";
import {PopupTriggerRestService} from "@/services/rest/cms-popup/PopupTriggerRestService";
import VisibilityRuleWrapper from "@/components/layouts/content-pages/popups/VisibilityRuleWrapper.vue";
import {PopupVisibilityRuleRestService} from "@/services/rest/cms-popup/PopupVisibilityRuleRestService";
import {I18nContext} from "@/context/I18nContext";
import {ContentPageRestService} from "@/services/rest/cms-content-page/ContentPageRestService";
import {ContentPageCreateRestDto} from "@/api/pharma-cms-content-page/models";
import {StatusEnum} from "@/api/enums/status-enum";
import {ContentBlockTypesEnum} from "@/helpers/functions/enum";
import Association from "@/components/layouts/content-pages/popups/Association.vue";
import {PopupAssociationRestService} from "@/services/rest/cms-popup/PopupAssociationRestService";
import InputSelect from "@/components/UI/InputSelect.vue";
import {VisibilityRuleTypeEnum} from "@/api/enums/visibility-rule-type-enum";
import {PopupAssociationTypeEnum} from "@/api/enums/popup-association-type-enum";
import association from "@/components/layouts/content-pages/popups/Association.vue";
import {FullPopupRestDtoModel} from "@/models/api/pharma-cms-popup/FullPopupRestDtoModel";
import {PopupButtonEnum} from "@/api/enums/popup-button-enum";

export default defineComponent({
  name: "PopupMetaData",
  components: {
    InputSelect,
    Association,
    VisibilityRuleWrapper,
    Trigger,
    BaseOffcanvas,
    BaseOffcanvasButtonOpen,
    InputText,
    InputCheckboxMultiSelect,
    AlertError2
  },
  props: {
    id: {
      type: String,
    },
  },
  data() {
    return {
      popupSizeOptions: [] as CodeDescriptionRestDto[],
      popupVerticalPositionOptions: [] as CodeDescriptionRestDto[],
      popupLayoutOptions: [] as CodeDescriptionRestDto[],
      contentBlockTypeOptions: [] as CodeDescriptionRestDto[],
      popupButtonOptions: [] as CodeDescriptionRestDto[],
      popupChanged: false,
      toaster: useToast(),
      pageUI: UIStateDto.createWithDefaults(),
      popup: FullPopupRestDtoModel.createWithDefaults() as FullPopupRestDto,
      triggers: null as PopupTriggerOverviewResultsRestDto | null,
      triggerMode: PageDetailModeEnum.NEW as PageDetailModeEnum,
      triggerId: null as string | null,
      triggerType: null as PopupTriggerTypeEnum | null,
      // The triggerSequence is used to force a remount of the Trigger component in the offcanvas,
      // So the offcanvas always opens with fresh data
      triggerSequence: 0 as number,
      visibilityRules: null as PopupVisibilityRuleOverviewResultsRestDto | null,
      visibilityRuleMode: PageDetailModeEnum.NEW as PageDetailModeEnum,
      visibilityRuleId: null as string | null,
      // The visibilityRuleSequence is used to force a remount of the VisibilityRule component in the offcanvas,
      // So the offcanvas always opens with fresh data
      visibilityRuleSequence: 0 as number,
      associations: null as PopupAssociationOverviewResultsRestDto | null,
      associationMode: PageDetailModeEnum.NEW as PageDetailModeEnum,
      associationId: null as string | null,
      // The associationsSequence is used to force a remount of the Association component in the offcanvas,
      // So the offcanvas always opens with fresh data
      associationSequence: 0 as number,
    }
  },
  mounted() {
    this.reloadContent();
  },
  computed: {
    association() {
      return association
    },
    PopupAssociationTypeEnum() {
      return PopupAssociationTypeEnum;
    },
    PopupButtonEnum() {
      return PopupButtonEnum;
    },
    VisibilityRuleTypeEnum() {
      return VisibilityRuleTypeEnum;
    },
    I18nContext() {
      return I18nContext;
    },
    PageDetailModeEnum() {
      return PageDetailModeEnum;
    },
    PopupTriggerTypeEnum() {
      return PopupTriggerTypeEnum;
    },
  },
  methods: {
    truncateString,
    isEmptyString,
    async submitData() {
      this.pageUI
        .clearError()
        .setNotReady();

      try {
        await PopupRestService.getInstance().updatePopup(this.id, this.popup.popup);
        await this.afterSave();
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.pageUI.setReady();
      }
    },
    async reloadContent() {
      this.pageUI
        .clearError()
        .setNotReady();
      try {
        this.popup = await PopupRestService.getInstance().getPopup(this.id);
        this.triggers = await PopupTriggerRestService.getInstance().getTriggers(this.id);
        this.visibilityRules = await PopupVisibilityRuleRestService.getInstance().getVisibilityRules(this.id);
        this.associations = await PopupAssociationRestService.getInstance().getAssociations(this.id);
        this.popupSizeOptions =
          await EnumRestService.getInstance().getCmsEnumValuesByClassname(EnumClassNameCmsEnum.POPUP_SIZE);
        this.popupVerticalPositionOptions =
          await EnumRestService.getInstance().getCmsEnumValuesByClassname(EnumClassNameCmsEnum.POPUP_VERTICAL_POSITION);
        this.popupLayoutOptions =
          await EnumRestService.getInstance().getCmsEnumValuesByClassname(EnumClassNameCmsEnum.POPUP_LAYOUT);
        this.contentBlockTypeOptions =
          await EnumRestService.getInstance().getCmsEnumValuesByClassname(EnumClassNameCmsEnum.CONTENT_BLOCK_TYPE);
        this.popupButtonOptions =
          await EnumRestService.getInstance().getCmsEnumValuesByClassname(EnumClassNameCmsEnum.POPUP_BUTTON);

        this.popupChanged = false;
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.pageUI.setReady();
      }
    },
    async afterSave(message: string | null = null) {
      this.toaster.success(message ?? this.$t('savedSuccessfully'));
      await this.reloadContent();
    },
    onPopupChanged(): void {
      this.popupChanged = true;
    },
    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
        .create()
        .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .catch(exceptions, true);
    },
    prepareOffCanvasForNewTrigger(): void {
      this.triggerId = null;
      this.triggerType = null;
      this.triggerMode = PageDetailModeEnum.NEW;
      this.triggerSequence++;
    },
    prepareOffCanvasForEditTrigger(trigger: PopupTriggerOverviewRestDto): void {
      this.triggerId = trigger.id;
      this.triggerType = trigger.type.code as PopupTriggerTypeEnum;
      this.triggerMode = PageDetailModeEnum.EDIT;
      this.triggerSequence++;
    },
    prepareOffCanvasForNewVisibilityRule(): void {
      this.visibilityRuleId = null;
      this.visibilityRuleMode = PageDetailModeEnum.NEW;
      this.visibilityRuleSequence++;
    },
    prepareOffCanvasForEditVisibilityRule(visibilityRule: PopupVisibilityRulesOverviewRestDto): void {
      this.visibilityRuleId = visibilityRule.id;
      this.visibilityRuleMode = PageDetailModeEnum.EDIT;
      this.visibilityRuleSequence++;
    },
    prepareOffCanvasForNewAssociation(): void {
      this.associationId = null;
      this.associationMode = PageDetailModeEnum.NEW;
      this.associationSequence++;
    },
    prepareOffCanvasForEditAssociation(association: PopupAssociationOverviewRestDto): void {
      this.associationId = association.id;
      this.associationMode = PageDetailModeEnum.EDIT;
      this.associationSequence++;
    },
    getAssociationDescription(association: PopupAssociationOverviewRestDto): string {
      if (association.type.code === PopupAssociationTypeEnum.NAMED_PAGE) {
        return this.$t('homepage');
      }

      return association.type.description + (association.description
          ? ' (' + truncateString(association.description, 50) + ')'
          : ''
      )
    },
    buttonChanged(): void {
      if (this.popup.popup!.buttons.includes(PopupButtonEnum.DO_NOT_SHOW_AGAIN)
        && !this.popup.popup!.do_not_show_duration_in_sec) {
        this.popup.popup!.do_not_show_duration_in_sec = 60 * 60 * 24 * 7;
      }
    },
    async deleteTrigger(trigger: PopupTriggerOverviewRestDto): Promise<void> {
      const answer = window.confirm(this.$t('deleteConfirmation'));

      if (answer) {
        this.pageUI
          .setNotReady()
          .clearError();

        try {
          switch (trigger.type.code) {
            case PopupTriggerTypeEnum.EXIT_INTENT_TRIGGER:
              await PopupTriggerRestService.getInstance().deletePopupExitIntentTrigger(this.id);
              break;
            case PopupTriggerTypeEnum.CODE_TRIGGER:
              await PopupTriggerRestService.getInstance().deletePopupCodeTrigger(trigger.id);
              break;
            case PopupTriggerTypeEnum.TIME_DELAY_TRIGGER:
              await PopupTriggerRestService.getInstance().deletePopupTimeDelayTrigger(trigger.id);
              break;
          }

          await this.afterSave(this.$t('deletedSuccessfully'));
        } catch (exceptions: any) {
          this.setExceptions(exceptions);
        } finally {
          this.pageUI.setReady();
        }
      }
    },
    async deleteVisibilityRule(visibilityRule: PopupVisibilityRulesOverviewRestDto): Promise<void> {
      const answer = window.confirm(this.$t('deleteConfirmation'));

      if (answer) {
        this.pageUI
          .setNotReady()
          .clearError();

        try {
          await PopupVisibilityRuleRestService.getInstance().deletePopupVisibilityRule(visibilityRule.id);
          await this.afterSave(this.$t('deletedSuccessfully'));
        } catch (exceptions: any) {
          this.setExceptions(exceptions);
        } finally {
          this.pageUI.setReady();
        }
      }
    },
    async deleteAssociation(association: PopupAssociationOverviewRestDto): Promise<void> {
      const answer = window.confirm(this.$t('deleteConfirmation'));

      if (answer) {
        this.pageUI
          .setNotReady()
          .clearError();

        try {
          await PopupAssociationRestService.getInstance().deleteAssociation(association.id);
          await this.afterSave(this.$t('deletedSuccessfully'));
        } catch (exceptions: any) {
          this.setExceptions(exceptions);
        } finally {
          this.pageUI.setReady();
        }
      }
    },
    async openContent(language: string): Promise<void> {
      if (!this.popup.popup) {
        return;
      }

      this.pageUI
        .setNotReady()
        .clearError();

      try {
        if (!this.popup.popup!.body_page[language as keyof MultilingualMax50FieldRestDto]) {
          const pageCreateRestDto: ContentPageCreateRestDto = {
            status_code: this.popup.popup!.status_code ?? StatusEnum.NOT_PUBLISHED,
            name: this.popup.popup!.name + ' (' + language + ')',
            type_code: ContentBlockTypesEnum.POPUP_CONTENT
          };
          this.popup.popup!.body_page[language as keyof MultilingualMax50FieldRestDto] =
            await ContentPageRestService.getInstance().createContentPage(pageCreateRestDto);
          await PopupRestService.getInstance().updatePopup(this.id, this.popup.popup);
        }

        this.$router.push({
          name: 'edit-popup-wysiwyg',
          params: {
            id: this.popup.popup!.body_page[language as keyof MultilingualMax50FieldRestDto],
            popupId: this.id
          }
        });
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
      } finally {
        this.pageUI.setReady();
      }
    }
  },
});
